import { hasValue, tokenHasExpired } from 'swipeclock-frontend';

export function deferLoading(store, data, fetchDeferredData) {
  return {
    ...data,
    root: new Promise((resolve, reject) => {
      (async () => {
        try {
          // Perform any custom deferred loading here.
          const payload = await fetchDeferredData(store);
          resolve({
            ...payload,
          });
        } catch (error) {
          reject(error);
        }
      })(); // Invoke the async function.
    }),
  };
}

/**
 * Generic function to load a part of the state from the Redux store.
 * See how it's used down below for examples.
 * @param {Object} store - The Redux store.
 * @param {Function} selector - Function to select the part of the state to load.
 * @param {Function} validator - Function to validate the selected part of the state.
 * @param {string} errorMessage - Error message for the timeout error.
 * @returns {Promise} A promise that resolves with the selected part of the state if it's valid, or rejects with a timeout error if it's not valid within 5 seconds.
 */
function loadFromStore(store, selector, validator, errorMessage) {
  return new Promise((resolve, reject) => {
    const currentState = selector(store.getState());
    if (validator(currentState)) {
      resolve(currentState);
      return;
    }

    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error(errorMessage));
    }, 5000); // 5 seconds timeout

    const unsubscribe = store.subscribe(() => {
      const statePart = selector(store.getState());
      if (validator(statePart)) {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(statePart);
      }
    });
  });
}

/**
 * Function to load the user from the Redux store.
 * @param {Object} store - The Redux store.
 * @returns {Promise} A promise that resolves with the user if the user has a valid token, or rejects with a timeout error if the user doesn't have a valid token within 5 seconds.
 */
export function loadUserFromStore(store) {
  return loadFromStore(
    store,
    (state) => state.user,
    (user) => user?.token && !tokenHasExpired(user),
    'Timeout: User token did not resolve in time'
  );
}

/**
 * Function to load the navigation from the Redux store.
 * @param {Object} store - The Redux store.
 * @returns {Promise} A promise that resolves with the navigation if it has a topAppBar, or rejects with a timeout error if it doesn't have a topAppBar within 5 seconds.
 */
export function loadTopAppBarFromStore(store) {
  return loadFromStore(
    store,
    (state) => state.navigation,
    (navigation) => hasValue(navigation.topAppBar),
    'Timeout: Navigation did not resolve in time'
  );
}

export function loadDrawersFromStore(store) {
  return loadFromStore(
    store,
    (state) => state.navigation,
    (navigation) => hasValue(navigation.drawers),
    'Timeout: Drawer did not resolve in time'
  );
}

export function loadLogonUrlsFromStore(store) {
  return loadFromStore(
    store,
    (state) => state.app,
    (app) => app.logonUrlIsSet,
    'App: logon url is not set'
  );
}
